/**
 * Zoho sync queue — every change staff make in the app goes through here, so the top-bar
 * indicator can say honestly whether it has reached Zoho.
 *
 *   change   → queued (saved on the phone, survives closing the app)
 *            → syncing → synced            (Zoho confirmed it)
 *                      → failed            (error shown, retried automatically, or tap Retry)
 *   offline  → waits, then goes through when the phone is back online
 *
 * Changes are pushed one at a time, oldest first — order matters (a reschedule after a cancel).
 *
 *   REACT_APP_SYNC_PROVIDER=mock  simulated Zoho (default — Zoho isn't connected yet)
 *   REACT_APP_SYNC_PROVIDER=api   POST each change to the server, which writes it to Zoho CRM:
 *
 *   POST /api/sync   { id, type, leadId, payload }  → 200 { zohoId? }   (idempotent on id)
 *     lead.status          → Deal Stage + a Note with the reason
 *     lead.reschedule      → appointment start / end on the Deal (+ Note)
 *     followup.book        → Deal stage "Job Booked"
 *     followup.dismiss     → Deal stage "Dismissed" + Note with the reason
 *     followup.schedule    → next follow-up date (+ Note)
 *     followup.note        → Note on the Deal
 *     followup.create      → follow-up on the quoted Deal (next call date)
 *     lead.note            → Note on the Deal
 *     quote.sent           → Zoho Quotes for a quote-only send (no inspection)
 *     followup.noAnswer    → Call activity "No answer"
 *     inspection.sent      → Zoho Quotes for each package + the report link on the Deal
 *
 * LIVE NOW — route changes on the Zoho Inspection go to n8n "[C] App 2 - Update Inspection"
 * (REACT_APP_INSPECTION_UPDATE_URL), whatever REACT_APP_SYNC_PROVIDER says. Those changes carry
 * a `zoho` body { action, inspectionId, note, date, start, amount }:
 *   cancel → Cancelled · rebook → Rebooking · reschedule → new window · report_sent → Deal
 *   "Report/Quote Sent / Follow Up" + amount · note → Note. Every one adds a signed Note.
 * Zoho refusing a change (slot taken, no Deal…) is final — shown, not retried. No signal is retried.
 *
 * LIVE NOW — inspection submit (Next → quote) goes to n8n "[C] App 4 - Submit Inspection" via
 * services/inspectionSubmit.js, which registers the 'submit' action below.
 */
import { useSyncExternalStore } from 'react';
import { getAccessToken } from './auth';

export const SYNC_PROVIDER = process.env.REACT_APP_SYNC_PROVIDER || 'mock';
const API_URL = process.env.REACT_APP_SYNC_API || '/api/sync';
const STORAGE_KEY = 'cie_sync_outbox_v1';
const KEEP_SYNCED = 30; // how many finished changes to keep for the details panel
const RETRY_MS = [5000, 15000, 60000, 300000];

/* ---------- state (saved on the phone) ---------- */
const load = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    // anything that was mid-sync when the app closed goes back in the queue
    return saved.map((c) => (c.status === 'syncing' ? { ...c, status: 'queued' } : c));
  } catch {
    return [];
  }
};

let changes = load();
let online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
let snapshot = { changes, online };
const listeners = new Set();

const persist = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(changes));
  } catch {
    /* storage full / private mode — the queue still works for this session */
  }
};

const emit = () => {
  snapshot = { changes, online };
  persist();
  listeners.forEach((fn) => fn());
};

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const setChange = (id, patch) => {
  changes = changes.map((c) => (c.id === id ? { ...c, ...patch } : c));
  emit();
};

/* ---------- providers ---------- */
const mockProvider = {
  async push() {
    await new Promise((r) => setTimeout(r, 700));
    return { zohoId: null };
  },
};

const apiProvider = {
  async push(change) {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id: change.id, type: change.type, leadId: change.leadId, payload: change.payload }),
    });
    if (!res.ok) throw new Error((await res.text().catch(() => '')) || `Zoho sync failed (${res.status})`);
    return res.json().catch(() => ({}));
  },
};

const provider = SYNC_PROVIDER === 'api' ? apiProvider : mockProvider;

/* ---------- live: Zoho Inspection changes through n8n ---------- */
const INSPECTION_URL = process.env.REACT_APP_INSPECTION_UPDATE_URL;
export const INSPECTION_SYNC_LIVE = !!INSPECTION_URL;

/**
 * Send one Inspection change to Zoho now. Resolves with what Zoho saved.
 * Rejects with err.offline = true when it never reached n8n (no signal) — safe to queue and retry;
 * any other rejection is Zoho saying no, with a message staff can act on.
 */
export const sendInspectionUpdate = async (body) => {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again to save changes to Zoho.');
  let res;
  try {
    res = await fetch(INSPECTION_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (e) {
    const err = new Error('No connection — it will be sent to Zoho when you’re back online.');
    err.offline = true;
    throw err;
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) {
    const err = new Error(out.error || `Zoho didn’t save it (${res.status}).`);
    err.offline = res.status >= 500; // Zoho/n8n hiccup: retry · 4xx (slot taken, no Deal, signed out): final
    throw err;
  }
  return out;
};

// other n8n endpoints register how their changes go out (e.g. inspectionSubmit → 'submit')
const zohoActions = new Map();
export const registerZohoAction = (action, push) => zohoActions.set(action, push);

const zohoProvider = {
  async push(change) {
    const custom = zohoActions.get(change.zoho.action);
    if (custom) return custom(change.zoho, getAccessToken);
    return sendInspectionUpdate({ ...change.zoho, changeId: change.id });
  },
};
const isLive = (change) => !!(change.zoho && (zohoActions.has(change.zoho.action) || INSPECTION_SYNC_LIVE));
const providerFor = (change) => (isLive(change) ? zohoProvider : provider);

/* ---------- the engine ---------- */
let running = false;
let retryTimer = null;

const next = () =>
  changes.find((c) => c.status === 'queued' || (c.status === 'failed' && c.retryAt && Date.now() >= c.retryAt));

const run = async () => {
  if (running || !online) return;
  running = true;
  try {
    for (let c = next(); c && online; c = next()) {
      setChange(c.id, { status: 'syncing', error: null });
      try {
        const res = await providerFor(c).push(c);
        setChange(c.id, { status: 'synced', syncedAt: new Date().toISOString(), zohoId: (res && res.zohoId) || null, retryAt: null });
      } catch (err) {
        const attempts = (c.attempts || 0) + 1;
        const wait = RETRY_MS[Math.min(attempts - 1, RETRY_MS.length - 1)];
        // Zoho saying no (e.g. the time was taken) won't change on a retry — wait for a person
        const final = isLive(c) && err.offline === false;
        setChange(c.id, { status: 'failed', attempts, error: err.message || 'Could not reach Zoho', retryAt: final ? null : Date.now() + wait });
      }
    }
    // keep the panel short — drop the oldest finished changes
    const done = changes.filter((c) => c.status === 'synced');
    if (done.length > KEEP_SYNCED) {
      const drop = new Set(done.slice(0, done.length - KEEP_SYNCED).map((c) => c.id));
      changes = changes.filter((c) => !drop.has(c.id));
      emit();
    }
  } finally {
    running = false;
  }
  scheduleRetry();
};

const scheduleRetry = () => {
  clearTimeout(retryTimer);
  const waiting = changes.filter((c) => c.status === 'failed' && c.retryAt).map((c) => c.retryAt);
  if (waiting.length) retryTimer = setTimeout(run, Math.max(1000, Math.min(...waiting) - Date.now()));
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    online = true;
    emit();
    run();
  });
  window.addEventListener('offline', () => {
    online = false;
    emit();
  });
  // resume whatever was waiting when the app was last closed
  setTimeout(run, 0);
}

/* ---------- what the app uses ---------- */

/**
 * Record a change for Zoho. Call it from the data layers whenever staff change something.
 * label: what the details panel shows, e.g. "John Smith — Cancelled"
 */
/**
 * supersede: a key like "submit:<id>" — a newer change with the same key replaces any older one
 * that hasn't gone out yet (a redone inspection only needs its latest version sent).
 */
export const queueChange = ({ type, leadId = null, label, payload = {}, zoho = null, synced = false, supersede = null }) => {
  const now = new Date().toISOString();
  const change = {
    id: `chg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    leadId,
    label,
    payload,
    zoho, // set → a real Zoho write (n8n), not simulated
    live: !!(zoho && (zohoActions.has(zoho.action) || INSPECTION_SYNC_LIVE)),
    supersede,
    // already saved in Zoho by the caller (sent straight away) — logged for the panel only
    status: synced ? 'synced' : 'queued',
    syncedAt: synced ? now : undefined,
    attempts: 0,
    createdAt: now,
  };
  const waiting = (c) => c.status === 'queued' || c.status === 'failed';
  changes = [...changes.filter((c) => !(supersede && c.supersede === supersede && waiting(c))), change];
  emit();
  run();
  return change.id;
};

export const retryChange = (id) => {
  setChange(id, { status: 'queued', retryAt: null });
  run();
};

export const syncNow = () => {
  changes = changes.map((c) => (c.status === 'failed' ? { ...c, status: 'queued', retryAt: null } : c));
  emit();
  run();
};

export const getSyncState = () => snapshot;
export const useSync = () => useSyncExternalStore(subscribe, getSyncState);

// one word for the indicator
export const syncSummary = ({ changes: list, online: isOnline }) => {
  const failed = list.filter((c) => c.status === 'failed').length;
  const waiting = list.filter((c) => c.status === 'queued' || c.status === 'syncing').length;
  if (failed) return { state: 'failed', count: failed };
  if (!isOnline && waiting) return { state: 'offline', count: waiting };
  if (waiting) return { state: 'syncing', count: waiting };
  return { state: 'synced', count: 0 };
};
